import React from 'react';
import { Grid, FlexboxGrid, Row, Col, Text } from 'rsuite';
import pluralize from 'pluralize';
import BasicStyledCheckbox from '../../../form/BasicStyledCheckbox';
import TaxReportingStatusIndicator from '../../TaxReportingStatusIndicator';
import { getAddressWithUnitNumber, titleize } from '../../../../common/utils';
import { PaymentPeriod, PeriodLicense } from '../../../../types/tax';
import IconIndicator from '../../../user-feedback/IconIndicator';

interface TaxLicensesTableProps {
  licenses?: Array<PeriodLicense>;
  loading?: boolean;
  period: PaymentPeriod;
  permitType?: string;
  selectAll: (period: PaymentPeriod, isSelected: boolean) => void;
  selectLicense: (licenseNum: string, period: PaymentPeriod) => void;
  selectedLicenses?: Set<string>;
}

export const TaxLicensesTable = ({
  licenses = [],
  loading = false,
  period,
  permitType = '',
  selectAll,
  selectLicense,
  selectedLicenses = new Set<string>(),
}: TaxLicensesTableProps) => {
  if (loading) {
    return <IconIndicator />;
  }
  if (licenses?.length === 0) {
    return <Text>No {pluralize(permitType)} available for this period</Text>;
  }
  return (
    <Grid
      fluid
      className="tax-table"
    >
      <FlexboxGrid
        className="tax-column-heading-row"
        align="bottom"
        as={Row}
      >
        <FlexboxGrid.Item
          as={Col}
          xs={24}
          md={2}
        >
          <div role="grid">
            <FlexboxGrid
              as={Row}
              align="bottom"
              gutter={0}
            >
              <FlexboxGrid.Item as={Col}>
                <BasicStyledCheckbox
                  label=""
                  onChange={(checked: boolean) => {
                    selectAll(period, checked);
                  }}
                />
              </FlexboxGrid.Item>
              <FlexboxGrid.Item
                as={Col}
                mdHidden
                lgHidden
                xlHidden
                xxlHidden
              >
                Select/Unselect all
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </div>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item
          as={Col}
          xs={22}
          xsHidden
          smHidden
          mdHidden={false}
        >
          <div role="grid">
            <FlexboxGrid
              as={Row}
              align="bottom"
            >
              <FlexboxGrid.Item
                as={Col}
                xs={22}
                md={9}
              >
                Address
              </FlexboxGrid.Item>
              <FlexboxGrid.Item
                as={Col}
                xs={12}
                md={5}
              >
                {permitType} Number
              </FlexboxGrid.Item>
              <FlexboxGrid.Item
                as={Col}
                xs={6}
                md={5}
              >
                Tax Reporting This Period
              </FlexboxGrid.Item>
              <FlexboxGrid.Item
                as={Col}
                xs={6}
                md={5}
              >
                Owner&apos;s Name
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </div>
        </FlexboxGrid.Item>
      </FlexboxGrid>
      <div className="tax-license-row-wrapper">
        {licenses.map((license, index) => (
          <Row
            key={`license_${index}`}
            className="tax-license-row"
          >
            <Col
              xs={3}
              md={2}
            >
              <BasicStyledCheckbox
                label=""
                onChange={() => {
                  selectLicense(license.license ?? '', period);
                }}
                checked={selectedLicenses.has(license.license ?? '')}
              />
            </Col>
            <Col
              xs={21}
              md={22}
            >
              <div role="grid">
                <Row>
                  <Col
                    xs={22}
                    md={9}
                    className="select-properties-address"
                  >
                    {getAddressWithUnitNumber(
                      license.address,
                      license.unitNumber
                    )}
                  </Col>
                  <Col
                    xs={12}
                    md={5}
                  >
                    {license.license}
                  </Col>
                  <Col
                    xs={12}
                    md={5}
                  >
                    <TaxReportingStatusIndicator
                      status={license.totPaidStatus}
                    />
                    {titleize(license.totPaidStatus ?? '')}
                  </Col>
                  <Col
                    xs={24}
                    md={5}
                  >
                    {license.ownerName}
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        ))}
      </div>
    </Grid>
  );
};
